import React, { useContext, useEffect, useRef, useState } from 'react';

import { AppContext } from 'contexts';
import { makeStyles, Box, Modal, Typography, Button, TextField } from '@material-ui/core';
import ReCAPTCHA from 'react-google-recaptcha';
import { KEYS } from 'config/constants';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { startsWith } from 'lodash';

const useStyles = makeStyles((theme) => {
  return {
    login: {
      width: 300,
      position: 'relative',
      top: '20%',
      marginLeft: 'auto',
      marginRight: 'auto',
      alignContent: 'center',
      backgroundColor: 'white',
      padding: 30,
      animation: 'slide-down .2s ease-in',

      '@media (max-width: 767px)': {
        top: 101,
      },
    },
    row: {
      height: '100%',
      marginBottom: 20,
      flexDirection: 'row',
      display: 'flex',
      alignItems: 'center',
    },
    title: {
      marginLeft: 5,
    },
    backIcon: {
      cursor: 'pointer',
    },
    heading: {
      textAlign: 'center',
      margin: theme.spacing(1, 0, 4),
    },
    submitButton: {
      marginTop: theme.spacing(4),
    },
    textButton: {
      fontSize: '12px',
      marginBottom: 5,
    },
    button: {
      fontSize: '12px',
      color: 'white',
      marginTop: 5,
      marginBottom: 5,
    },
    verifyButton: {
      marginTop: 10,
    },
    resendMessage: {
      flexDirection: 'row',
    },
    warning: {
      fontSize: 12,
      color: 'grey',
    },
    error: {
      color: 'red',
      fontSize: 12,
    },
    modalTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      marginBottom: 10,
      textAlign: 'center',
    },
  };
});
function RequestVisitModal() {
  const appContext = useContext(AppContext);
  const recaptchaRef = useRef(null);
  const classes = useStyles();
  const { requestVisitModalOpen, requestVisit, showRequestVisitModal } = appContext;
  const [firstname, setFirstName] = useState('');
  const [lastname, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [errors, setErrors] = useState({
    firstname: false,
    lastname: false,
    captchaError: false,
    email: false,
    phoneNumber: false,
  });
  const onCaptchaChange = (value) => {
    setIsVerified(true);
    setErrors({ ...errors, captchaError: false });
  };
  const onCaptchaExpired = () => {
    setIsVerified(false);
    setErrors({ ...errors, captchaError: true });
  };
  const onCaptchaErrored = () => {
    setIsVerified(false);
    setErrors({ ...errors, captchaError: true });
  };
  const validatePhoneNumber = (inputNumber, country, countries) => {
    return countries.some(() => {
      return (
        startsWith(inputNumber, country.dialCode) ||
        startsWith(country.dialCode, inputNumber)
      );
    });
  };
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };
  const validatePhoneNumberLength = () => {
    // Basic validation: consider valid if there are at least 10 digits (NA format) when provided
    const digits = (phoneNumber || '').replace(/\D/g, '');
    const provided = digits.length > 0;
    const valid = !provided || digits.length >= 10;
    setErrors((prev) => {
      return { ...prev, phoneNumber: provided && !valid };
    });
    return valid;
  };
  const validateEmail = (input) => {
    let isValid;
    // if email is not empty, validate format
    if (email) {
      const re = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
      isValid = re.test(String(email).toLowerCase());
      if (!isValid) {
        setErrors({ ...errors, email: true });
      } else {
        setErrors({ ...errors, email: false });
      }
    } else {
      // Email is required
      setErrors({ ...errors, email: true });
      isValid = false;
    }
    return isValid;
  };
  const onBlurFirstName = () => {
    const isValid = firstname.trim() !== '';
    setErrors({ ...errors, firstname: !isValid });
  };
  const onBlurLastName = () => {
    const isValid = lastname.trim() !== '';
    setErrors({ ...errors, lastname: !isValid });
  };
  const closeModal = () => {
    showRequestVisitModal(false);
  };
  let emailHelperText = '';
  if (errors.email) {
    emailHelperText = email.trim() === '' ? 'Email requis' : 'Email invalide';
  }
  const onSubmit = () => {
    const isFirstNameValid = firstname.trim() !== '';
    const isLastNameValid = lastname.trim() !== '';

    // Email is required and must be valid
    const emailProvided = email.trim() !== '';
    const emailRegex = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
    const emailValidStrict = emailProvided && emailRegex.test(email.trim().toLowerCase());

    const phoneDigits = (phoneNumber || '').replace(/\D/g, '');
    const phoneProvided = phoneDigits.length > 0;
    const phoneValidStrict = phoneProvided && phoneDigits.length >= 10;

    // Update individual field errors
    const nextErrors = { ...errors };
    nextErrors.firstname = !isFirstNameValid;
    nextErrors.lastname = !isLastNameValid;

    // Email must be present and valid
    nextErrors.email = !emailValidStrict;
    // Phone is optional; flag only if provided and invalid
    nextErrors.phoneNumber = phoneProvided && !phoneValidStrict;

    nextErrors.captchaError = !isVerified;
    setErrors(nextErrors);

    if (isFirstNameValid && isLastNameValid && emailValidStrict && isVerified) {
      requestVisit(firstname, lastname, phoneNumber, email);
      setFirstName('');
      setLastName('');
      setPhoneNumber(null);
      setEmail('');
      setIsVerified(false);
      recaptchaRef.current.reset();
      closeModal();
    }
  };

  return (
    <div>
      <Modal
        open={requestVisitModalOpen}
        onClose={closeModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className={classes.login}>
          <Typography className={classes.modalTitle}>Demander une visite</Typography>
          <TextField
            variant='outlined'
            value={firstname}
            onChange={(e) => {
              return setFirstName(e.target.value);
            }}
            margin='normal'
            label='Prénom'
            fullWidth
            size='small'
            required
            onBlur={onBlurFirstName}
            error={errors.firstname}
            helperText={errors.firstname ? 'Prénom requis' : ''}
          />
          <TextField
            variant='outlined'
            value={lastname}
            onBlur={onBlurLastName}
            onChange={(e) => {
              return setLastName(e.target.value);
            }}
            margin='normal'
            label='Nom de famille'
            fullWidth
            size='small'
            required
            error={errors.lastname}
            helperText={errors.lastname ? 'Nom de famille requis' : ''}
          />
          <PhoneInput
            country='ca'
            preferredCountries={['ca', 'us']}
            enableSearch
            value={phoneNumber}
            inputProps={{
              name: 'Numéro de téléphone',
              required: true,
            }}
            prefix='+'
            countryCodeEditable={false}
            onChange={(phone) => {
              return setPhoneNumber(`+${phone}`);
            }}
            onBlur={(e) => {
              if (phoneNumber !== null) {
                return validatePhoneNumberLength();
              }
              setErrors({ ...errors, phoneNumber: false });
              return null;
            }}
            isValid={(inputNumber, country: any, countries) => {
              return validatePhoneNumber(inputNumber, country, countries);
            }}
            defaultErrorMessage='Numéro de téléphone invalide'
            containerStyle={{ marginBottom: 5, marginTop: 15 }}
            inputStyle={{ margin: 0, width: '100%', height: 40 }}
          />
          {errors.phoneNumber && (
            <Typography className={classes.error}>
              Numéro de téléphone invalide
            </Typography>
          )}
          <TextField
            variant='outlined'
            value={email}
            onChange={(e) => {
              return handleEmailChange(e);
            }}
            onBlur={(e) => {
              return validateEmail(e.target.value);
            }}
            required
            error={errors.email}
            helperText={emailHelperText}
            margin='normal'
            label='Email'
            fullWidth
            size='small'
          />
          <Box className={classes.row}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={KEYS.CAPTCHA_KEY}
              onChange={onCaptchaChange}
              onExpired={onCaptchaExpired}
              onErrored={onCaptchaErrored}
            />
          </Box>
          {errors.captchaError && (
            <Typography className={classes.error}>
              Veuillez compléter le captcha
            </Typography>
          )}
          <Button
            className={classes.button}
            fullWidth
            variant='contained'
            color='primary'
            onClick={onSubmit}
          >
            Demander une visite
          </Button>
        </Box>
      </Modal>
    </div>
  );
}
export default RequestVisitModal;
